<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import {
    validateDialogueLogCreate,
    validateDialogueLogEdit,
    type ValidationIssue,
  } from '@domain/dialogue-log/validation';
  import { buildDialogueLogFields } from '@domain/dialogue-log/form';
  import { createDialogueLog, patchDialogueLog } from '@shared/api/dialogue-logs';
  import { ApiError } from '@shared/api/client';
  import type {
    DialogueLog,
    DialogueLogCreateRequest,
    DialogueLogType,
  } from '@shared/types/dialogue-log';
  import type { Lawyer } from '@domain/lawyer/model';
  import { DIALOGUE_LOG_TYPE_OPTIONS } from '@shared/constants/choices';

  interface ProposalOption {
    id: string;
    name: string;
  }

  interface Props {
    open: boolean;
    firmId: string;
    /// 配下弁護士 (顧客側参加者マルチセレクト)。
    lawyers?: Lawyer[];
    /// 該当事務所の商談 (関連商談プルダウン、 任意)。
    proposals?: ProposalOption[];
    /// 起動元が弁護士詳細の場合、 その弁護士を顧客側参加者の初期選択にする
    /// (作成時のみ。 編集時は initial の参加者を初期値にする)。
    defaultLawyerId?: string | null;
    /// 'edit' は既存の手動対話エントリの編集 (PATCH)。 既定は作成。
    mode?: 'create' | 'edit';
    /// mode === 'edit' の編集対象。 開いた時にフォームへ初期表示する。
    initial?: DialogueLog | null;
    onClose: () => void;
    onCreated?: (entry: DialogueLog) => void;
    onUpdated?: (entry: DialogueLog) => void;
  }

  let {
    open,
    firmId,
    lawyers = [],
    proposals = [],
    defaultLawyerId = null,
    mode = 'create',
    initial = null,
    onClose,
    onCreated,
    onUpdated,
  }: Props = $props();

  let occurredAt = $state(toLocalInputValue(new Date()));
  let entryType = $state<DialogueLogType>('phone');
  let title = $state('');
  let body = $state('');
  let relatedOpportunityId = $state('');
  let selectedLawyerIds = $state<string[]>(defaultLawyerId ? [defaultLawyerId] : []);

  let submitting = $state(false);
  let issues = $state<ValidationIssue[]>([]);
  let serverError = $state<string | null>(null);

  const isEdit = $derived(mode === 'edit');
  const modalTitle = $derived(isEdit ? 'タイムラインを編集' : 'タイムラインに追加');
  const submitLabel = $derived(isEdit ? '保存' : '作成');
  const submittingLabel = $derived(isEdit ? '保存中…' : '作成中…');
  const formId = $derived(isEdit ? 'dialogue-log-edit-form' : 'dialogue-log-create-form');

  // 開いた時にフォームを初期化する (ContractCreateModal と同パターン)。
  // 編集は対象 entry の保存値、 作成は空フォーム + 現在時刻。
  let lastOpenSeen = $state(false);
  $effect(() => {
    if (open && !lastOpenSeen) {
      if (isEdit && initial) {
        applyInitial(initial);
      } else {
        reset();
      }
    }
    lastOpenSeen = open;
  });

  function toLocalInputValue(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  }

  function applyInitial(entry: DialogueLog) {
    occurredAt = toLocalInputValue(new Date(entry.occurred_at));
    entryType = entry.type;
    title = entry.title;
    body = entry.body ?? '';
    relatedOpportunityId = entry.related_opportunity_id ?? '';
    selectedLawyerIds = [...entry.participant_lawyer_ids];
    issues = [];
    serverError = null;
  }

  function reset() {
    occurredAt = toLocalInputValue(new Date());
    entryType = 'phone';
    title = '';
    body = '';
    relatedOpportunityId = '';
    selectedLawyerIds = defaultLawyerId ? [defaultLawyerId] : [];
    issues = [];
    serverError = null;
  }

  function close() {
    reset();
    onClose();
  }

  function toggle(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  // 対話方法 radiogroup のボタン要素 (roving tabindex のフォーカス移動先)。
  const typeButtons: HTMLButtonElement[] = [];

  // role="radiogroup" の標準キーボード操作: 矢印キーで選択を隣へ移し、
  // 移動先ボタンへフォーカスも移す (端は循環)。
  function handleTypeKeydown(ev: KeyboardEvent, index: number) {
    const delta =
      ev.key === 'ArrowRight' || ev.key === 'ArrowDown'
        ? 1
        : ev.key === 'ArrowLeft' || ev.key === 'ArrowUp'
          ? -1
          : 0;
    if (delta === 0) return;
    ev.preventDefault();
    const count = DIALOGUE_LOG_TYPE_OPTIONS.length;
    const next = (index + delta + count) % count;
    const nextOption = DIALOGUE_LOG_TYPE_OPTIONS[next];
    if (!nextOption) return;
    entryType = nextOption.value;
    typeButtons[next]?.focus();
  }

  function issueFor(field: keyof DialogueLogCreateRequest): string | undefined {
    return issues.find((i) => i.field === field)?.message;
  }

  function editErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.isNotFound) return 'タイムラインが見つかりません。再読み込みしてください。';
      if (err.isForbidden) return '編集権限がありません。';
    }
    return err instanceof Error ? err.message : String(err);
  }

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    const fields = buildDialogueLogFields({
      occurredAtInput: occurredAt,
      type: entryType,
      title,
      body,
      relatedOpportunityId,
      participantLawyerIds: selectedLawyerIds,
    });
    issues = isEdit
      ? validateDialogueLogEdit(fields)
      : validateDialogueLogCreate({ firm_id: firmId, ...fields });
    if (issues.length > 0) return;

    submitting = true;
    serverError = null;
    try {
      if (isEdit) {
        if (!initial) throw new Error('編集対象のタイムラインが見つかりません');
        const entry = await patchDialogueLog(initial.id, fields);
        onUpdated?.(entry);
      } else {
        const entry = await createDialogueLog({ firm_id: firmId, ...fields });
        onCreated?.(entry);
      }
      reset();
    } catch (err) {
      serverError = isEdit
        ? editErrorMessage(err)
        : err instanceof Error
          ? err.message
          : String(err);
    } finally {
      submitting = false;
    }
  }
</script>

<Modal {open} title={modalTitle} onClose={close}>
  <form id={formId} onsubmit={handleSubmit}>
    <label>
      <span>日時 <em>*</em></span>
      <input type="datetime-local" bind:value={occurredAt} required />
      {#if issueFor('occurred_at')}<small class="error">{issueFor('occurred_at')}</small>{/if}
    </label>
    <div class="field">
      <span id="entry-type-label">対話方法 <em>*</em></span>
      <div class="type-buttons" role="radiogroup" aria-labelledby="entry-type-label">
        {#each DIALOGUE_LOG_TYPE_OPTIONS as opt, i (opt.value)}
          <button
            type="button"
            role="radio"
            aria-checked={entryType === opt.value}
            class="type-button"
            class:selected={entryType === opt.value}
            tabindex={entryType === opt.value ? 0 : -1}
            bind:this={typeButtons[i]}
            onclick={() => (entryType = opt.value)}
            onkeydown={(ev) => handleTypeKeydown(ev, i)}
          >
            {opt.label}
          </button>
        {/each}
      </div>
      {#if issueFor('type')}<small class="error">{issueFor('type')}</small>{/if}
    </div>

    <fieldset>
      <legend>顧客側参加者 (弁護士) <em>*</em></legend>
      {#if lawyers.length > 0}
        <div class="checks">
          {#each lawyers as l (l.id)}
            <label class="check">
              <input
                type="checkbox"
                checked={selectedLawyerIds.includes(l.id)}
                onchange={() => (selectedLawyerIds = toggle(selectedLawyerIds, l.id))}
              />
              <span>{l.full_name}</span>
            </label>
          {/each}
        </div>
      {:else}
        <small class="muted">配下に弁護士が登録されていません。</small>
      {/if}
      {#if issueFor('participant_lawyer_ids')}
        <small class="error">{issueFor('participant_lawyer_ids')}</small>
      {/if}
    </fieldset>

    <label>
      <span>タイトル</span>
      <input
        bind:value={title}
        autocomplete="off"
        placeholder="未入力なら本文から自動生成"
      />
      {#if issueFor('title')}<small class="error">{issueFor('title')}</small>{/if}
    </label>
    <label>
      <span>本文</span>
      <textarea
        bind:value={body}
        rows="4"
        placeholder={entryType === 'email'
          ? 'メール本文をそのまま貼り付け'
          : '電話・面談メモ、メール本文などを入力'}
      ></textarea>
    </label>
    {#if proposals.length > 0}
      <label>
        <span>関連商談</span>
        <select bind:value={relatedOpportunityId}>
          <option value="">(指定なし)</option>
          {#each proposals as p (p.id)}
            <option value={p.id}>{p.name}</option>
          {/each}
        </select>
      </label>
    {/if}
    {#if serverError}<p class="server-error">{serverError}</p>{/if}
  </form>
  {#snippet footer()}
    <button type="button" onclick={close} disabled={submitting}>キャンセル</button>
    <button type="submit" form={formId} class="primary" disabled={submitting}>
      {submitting ? submittingLabel : submitLabel}
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  label,
  .field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  span em,
  legend em {
    color: #b91c1c;
    font-style: normal;
  }
  .muted {
    color: #6b7280;
    font-size: 0.8125rem;
  }
  input,
  select,
  textarea {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: white;
  }
  textarea {
    resize: vertical;
  }
  fieldset {
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 8px 10px;
    margin: 0;
  }
  legend {
    font-size: 0.8rem;
    color: #374151;
    padding: 0 4px;
  }
  .checks {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
  }
  .check {
    flex-direction: row;
    align-items: center;
    gap: 6px;
    font-size: 0.8125rem;
  }
  .check input {
    padding: 0;
  }
  .error {
    color: #b91c1c;
    font-size: 0.75rem;
  }
  .server-error {
    color: #b91c1c;
    margin: 0;
  }
  button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  /* 対話方法の単一選択ボタン群。 狭い幅では折り返し、 モーダル幅は広げない。 */
  .type-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .type-button {
    padding: 6px 14px;
    font-size: 0.875rem;
  }
  .type-button:not(.selected):hover {
    background: #f3f4f6;
  }
  .type-button:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 1px;
  }
  .type-button.selected {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
    font-weight: 600;
  }
</style>
